import { useState, useEffect, useRef } from "react"
import { useRouter } from "next/router"
import Otp from "components/atoms/Otp/Otp"
import Timer from "components/atoms/Timer/Timer"
import Button from "components/Form/Button"
import { useUserContext } from "context/UserContext"
import { useLang } from "hooks/useLang"
import { requestOtp, MessageType, OtpType } from "utils/helpers/requestOtp"
import Image from "next/image"
import InputNew from "components/Form/newAuthInput"
import useVerificationRequired from "hooks/useVerificationRequired"

interface IProps {
    value: string
    type: OtpType
    callingCode: string

    actionButtonsContainerClassName?: string
    isInsideModal?: boolean
    redirectPath?: string
}

const Verification = ({
    value,
    type,
    callingCode,
    actionButtonsContainerClassName,
    isInsideModal,
    redirectPath,
}: IProps) => {
    const { t } = useLang()
    const { login } = useUserContext()
    const { mutate: signinMutate, isPending } = login
    const router = useRouter()

    const [otp, setOtp] = useState(new Array(4).fill(""))
    const [password, setPassword] = useState("")
    const [showResend, setShowResend] = useState(false)
    const [verifyInputsFilled, setVerifyInputsFilled] = useState(false)
    const [isRateLimited, setIsRateLimited] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isVerified, setIsVerified] = useState(false)

    // Get verification flags to determine if OTP login should be available
    const {
        isVerificationRequired,
        isEmailVerificationRequired,
        isMobileVerificationRequired,
    } = useVerificationRequired()

    // Check if OTP login is available for the current input type
    const isOtpLoginAvailable =
        isVerificationRequired &&
        ((type === "email" && isEmailVerificationRequired) ||
            (type === "phone" && isMobileVerificationRequired))

    // Default to password, only use OTP if it's available
    const [verificationType, setVerificationType] = useState<
        "otp" | "password"
    >("password")

    const otpInputRef = useRef<HTMLInputElement | null>(null)
    const hasSentOtp = useRef(false) // Track if OTP has been sent, this is to prevent sending OTP twice in react strict mode (only in development)

    useEffect(() => {
        setVerifyInputsFilled(otp.join("").length === 4)
    }, [otp])

    useEffect(() => {
        // Only send OTP if OTP login is available and user switches to OTP mode
        if (
            !hasSentOtp.current &&
            verificationType === "otp" &&
            isOtpLoginAvailable
        ) {
            sendOtp(type)
            hasSentOtp.current = true
        }
    }, [verificationType, isOtpLoginAvailable])

    const sendOtp = async (inputType: OtpType) => {
        setShowResend(false)
        setOtp(new Array(4).fill(""))

        const response = await requestOtp({ value, inputType, callingCode })

        if (response.success) {
            setError(null)
        } else {
            if (response.messageType === MessageType.RATE_LIMIT_EXCEEDED) {
                setIsRateLimited(true)
            }
        }
    }

    const handleVerifyAndLogin = async (inputType: string) => {
        const otpString = otp.join("")
        signinMutate(
            inputType === "email"
                ? {
                      email: value,
                      password:
                          verificationType === "password"
                              ? password
                              : undefined,
                      otp: verificationType === "otp" ? otpString : undefined,
                  }
                : {
                      callingCode,
                      phone: value,
                      password:
                          verificationType === "password"
                              ? password
                              : undefined,
                      otp: verificationType === "otp" ? otpString : undefined,
                  },
            {
                onSuccess: (data: any) => {
                    if (data.status === 400 || data.error) {
                        if (data.message === "invalidOTP") {
                            setError(t("Invalid OTP"))
                        } else {
                            setError(data.message || t("login_error"))
                        }
                    } else {
                        setError(null)
                        setIsVerified(true) // Mark as verified
                        if (redirectPath) {
                            router.push(redirectPath)
                        }
                    }
                },
                onError: (error: any) => {
                    setError(t("something_went_wrong"))
                },
            }
        )
    }

    const showUserInfo = () => {
        return (
            <div className="flex flex-col items-start">
                {verificationType === "otp" ? (
                    <p className="text-4.5  text-[#7B91EF] font-bold">
                        {t("the_code_has_been_sent_to")}{" "}
                    </p>
                ) : (
                    <p className="text-4.5  text-[#7B91EF] font-bold">
                        {t("enterThePasswordFor")}{" "}
                    </p>
                )}
                <p className="text-[24px] text-[#fff] font-bold">{value}</p>
            </div>
        )
    }

    const handleVerificationTypeChange = (type: "otp" | "password") => {
        setVerificationType(type)
        setError(null)
        setShowResend(false)
        // setIsRateLimited(false)
        // setOtp(new Array(4).fill(""))
        setPassword("") // Reset password field
        setVerifyInputsFilled(false) // Reset verification inputs filled state
    }
    return (
        <div className="Form mx-4  flex flex-col items-start">
            <h1 className="text-center text-[2rem] font-bold">
                {/* {t("otp_has_been_sent_to", { value: "iejcdei" })} */}
            </h1>
            <div className="pt-4 text-center text-[1.3rem]">
                {showUserInfo()}
            </div>

            {verificationType === "otp" ? (
                <div className="pt-6 text-center">
                    <Otp
                        otp={otp}
                        setOtp={setOtp}
                        isFilled={verifyInputsFilled}
                        ref={otpInputRef}
                        isError={!!error}
                    />

                    {error && (
                        <span className="pt-2 text-xs text-red-500 dark:text-red-400 text-start items-center flex">
                            {error}
                        </span>
                    )}

                    {isRateLimited ? (
                        <div className="my-5 text-center text-red-600">
                            {t("rate_limit_exceeded_try_again_later")}
                        </div>
                    ) : showResend ? (
                        <div className="my-5 text-left">
                            <div className="text-[14px]">
                                {t("got_the_code")}
                                <span
                                    onClick={() => sendOtp(type)}
                                    className="mx-2 text-[#0DB9A0] text-[14px] hover:cursor-pointer hover:underline"
                                >
                                    {t("resend_code")}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="my-5 text-left">
                            <div className="inline-block text-sm text-blue-600">
                                <Timer
                                    initialMinute={1}
                                    initialSeconds={30}
                                    setShowResend={setShowResend}
                                    center
                                    hideText
                                    color="white"
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="w-full pt-4 pb-6">
                    <InputNew
                        placeholder="************"
                        label={t("password")}
                        inputID={"password_signup"}
                        name={"password"}
                        type={"password"}
                        onChange={(e) => setPassword(e.target.value)}
                        value={password}
                        error={error!}
                        reqStar
                    />
                </div>
            )}

            <div className="w-full pb-10 flex flex-col">
                <div className="w-full bg-[#F0F0F033] h-[1px]"></div>
                {verificationType === "otp" ? (
                    <div
                        onClick={() => handleVerificationTypeChange("password")}
                        className="flex cursor-pointer flex-row items-centers mt-3"
                    >
                        <Image
                            src={"/svg/password.svg"}
                            alt="success-image"
                            width={24}
                            height={24}
                        />
                        <span className="text-[#0DB9A0] text-[14px]">
                            {t("usePassword")}
                        </span>
                    </div>
                ) : // Only show OTP option if verification is enabled for this input type
                isOtpLoginAvailable ? (
                    <div
                        onClick={() => handleVerificationTypeChange("otp")}
                        className="flex cursor-pointer flex-row items-centers mt-3 gap-1"
                    >
                        <Image
                            src={"/svg/otp.svg"}
                            alt="success-image"
                            width={24}
                            height={24}
                        />
                        <span className="text-[#0DB9A0] text-[14px]">
                            {t("useOtp")}
                        </span>
                    </div>
                ) : null}
            </div>

            <div
                className={`mb-4 flex w-full flex-row items-center justify-between ${
                    actionButtonsContainerClassName
                        ? actionButtonsContainerClassName
                        : ""
                }`}
            >
                <Button
                    theme="modern"
                    text={t("continue")}
                    loading={isPending}
                    // disabled={
                    //     !verifyInputsFilled || isRateLimited || isVerified
                    // }
                    onClick={() => handleVerifyAndLogin(type)}
                    className={`px-8 p-3 ${isInsideModal ? "mx-4" : "flex-[1]"}`}
                />
            </div>
        </div>
    )
}

export default Verification
